import { logger } from '@lib/logger'
import type { LRUCache } from 'lru-cache'

const isServer = typeof window === 'undefined'
const MB = 1024 * 1024

interface MemoryStats {
    rss: number // Resident Set Size (total memory allocated)
    heapTotal: number // Total heap size
    heapUsed: number // Heap actually used
    external: number // C++ objects bound to JS
    arrayBuffers: number // ArrayBuffers and SharedArrayBuffers
}

interface FetchMetrics {
    totalFetchCalls: number
    fetchCallsSinceLastCheck: number
    lastResetTime: number
}

interface RegisteredCache {
    name: string
    cache: LRUCache<any, any>
}

class MemoryMonitor {
    private interval: NodeJS.Timeout | null = null
    private readonly intervalMs: number
    private lastStats: MemoryStats | null = null
    private readonly enabled: boolean
    private fetchMetrics: FetchMetrics = {
        totalFetchCalls: 0,
        fetchCallsSinceLastCheck: 0,
        lastResetTime: Date.now(),
    }
    private registeredCaches: RegisteredCache[] = []

    constructor(intervalMs: number = 60000) {
        // 1 minute default
        this.intervalMs = intervalMs
        // Only enable in production server-side
        this.enabled = isServer && process.env.NODE_ENV === 'production'
    }

    /**
     * Register an LRU cache for monitoring.
     * Registered caches will have their size logged in each memory cycle.
     */
    registerCache(name: string, cache: LRUCache<any, any>): void {
        this.registeredCaches.push({ name, cache })
    }

    /**
     * Start monitoring memory usage
     */
    start(): void {
        if (!this.enabled || this.interval) {
            return
        }

        logger.info('[Memory Monitor] Starting memory monitoring', {
            intervalMs: this.intervalMs,
        })

        // Log initial state
        this.logMemoryUsage()

        // Set up periodic logging
        this.interval = setInterval(() => {
            this.logMemoryUsage()
        }, this.intervalMs)

        // Don't keep process alive just for monitoring
        this.interval.unref()
    }

    /**
     * Stop monitoring
     */
    stop(): void {
        if (this.interval) {
            clearInterval(this.interval)
            this.interval = null
        }
        logger.info('[Memory Monitor] Stopped memory monitoring')
    }

    /**
     * Force immediate memory usage log (useful for debugging)
     */
    logNow(): void {
        if (!isServer) return
        this.logMemoryUsage()
    }

    /**
     * Get current memory stats (without logging)
     */
    getStats(): MemoryStats | null {
        if (!isServer) return null

        const usage = process.memoryUsage()
        return {
            rss: usage.rss,
            heapTotal: usage.heapTotal,
            heapUsed: usage.heapUsed,
            external: usage.external,
            arrayBuffers: usage.arrayBuffers,
        }
    }

    /**
     * Track a fetch call (for memory leak correlation analysis)
     * Call this from fetch wrappers to track fetch frequency
     */
    trackFetchCall(): void {
        if (!this.enabled) return

        this.fetchMetrics.totalFetchCalls++
        this.fetchMetrics.fetchCallsSinceLastCheck++
    }

    /**
     * Get fetch metrics
     */
    getFetchMetrics(): FetchMetrics {
        return { ...this.fetchMetrics }
    }

    /**
     * Collect metrics from all registered LRU caches
     */
    private getCacheMetrics(): Record<
        string,
        { size: number; max: number; calculatedSize: number }
    > {
        const metrics: Record<
            string,
            { size: number; max: number; calculatedSize: number }
        > = {}

        for (const { name, cache } of this.registeredCaches) {
            metrics[name] = {
                size: cache.size,
                max: cache.max,
                calculatedSize: cache.calculatedSize,
            }
        }

        return metrics
    }

    /**
     * Log current memory usage with delta from last measurement
     */
    private logMemoryUsage(): void {
        const usage = process.memoryUsage()

        const stats: MemoryStats = {
            rss: usage.rss,
            heapTotal: usage.heapTotal,
            heapUsed: usage.heapUsed,
            external: usage.external,
            arrayBuffers: usage.arrayBuffers,
        }

        const formattedStats = {
            rss: `${(stats.rss / MB).toFixed(2)} MB`,
            heapTotal: `${(stats.heapTotal / MB).toFixed(2)} MB`,
            heapUsed: `${(stats.heapUsed / MB).toFixed(2)} MB`,
            heapUsedPercent: `${((stats.heapUsed / stats.heapTotal) * 100).toFixed(1)}%`,
            external: `${(stats.external / MB).toFixed(2)} MB`,
            arrayBuffers: `${(stats.arrayBuffers / MB).toFixed(2)} MB`,
        }

        // Calculate delta if we have previous stats
        const delta = this.lastStats
            ? {
                  rss: `${((stats.rss - this.lastStats.rss) / MB).toFixed(2)} MB`,
                  heapTotal: `${((stats.heapTotal - this.lastStats.heapTotal) / MB).toFixed(2)} MB`,
                  heapUsed: `${((stats.heapUsed - this.lastStats.heapUsed) / MB).toFixed(2)} MB`,
                  external: `${((stats.external - this.lastStats.external) / MB).toFixed(2)} MB`,
              }
            : null

        const timeSinceLastCheck =
            (Date.now() - this.fetchMetrics.lastResetTime) / 1000
        const fetchRate =
            this.fetchMetrics.fetchCallsSinceLastCheck / timeSinceLastCheck

        // Collect cache metrics
        const cacheMetrics = this.getCacheMetrics()

        logger.info('[Memory Monitor] Memory usage', {
            memory: formattedStats,
            ...(delta && { delta }),
            fetch: {
                totalCalls: this.fetchMetrics.totalFetchCalls,
                callsSinceLastCheck: this.fetchMetrics.fetchCallsSinceLastCheck,
                callsPerSecond: fetchRate.toFixed(2),
            },
            ...(Object.keys(cacheMetrics).length > 0 && {
                caches: cacheMetrics,
            }),
        })

        // Reset fetch counter for next interval
        this.fetchMetrics.fetchCallsSinceLastCheck = 0
        this.fetchMetrics.lastResetTime = Date.now()

        // Alert if heap usage is very high
        // Skip under Bun/JSC where heapUsed > heapTotal is normal (different memory model)
        const heapUsedPercent = (stats.heapUsed / stats.heapTotal) * 100
        if (typeof Bun === 'undefined' && heapUsedPercent > 90) {
            logger.warn('[Memory Monitor] High heap usage detected', {
                heapUsedPercent: `${heapUsedPercent.toFixed(1)}%`,
                heapUsed: formattedStats.heapUsed,
                heapTotal: formattedStats.heapTotal,
            })
        }

        // Alert if significant growth since last measurement (> 50MB)
        if (
            this.lastStats &&
            stats.heapUsed - this.lastStats.heapUsed > 50 * MB
        ) {
            logger.warn('[Memory Monitor] Significant heap growth detected', {
                growth: delta?.heapUsed,
                heapUsed: formattedStats.heapUsed,
                fetchCallsInPeriod: this.fetchMetrics.fetchCallsSinceLastCheck,
                fetchRate: `${fetchRate.toFixed(2)}/sec`,
            })
        }

        // Alert if high fetch rate correlates with memory growth
        if (
            this.lastStats &&
            fetchRate > 10 &&
            stats.heapUsed - this.lastStats.heapUsed > 20 * MB
        ) {
            logger.warn(
                '[Memory Monitor] High fetch rate with memory growth detected',
                {
                    fetchRate: `${fetchRate.toFixed(2)}/sec`,
                    heapGrowth: delta?.heapUsed,
                    heapUsed: formattedStats.heapUsed,
                },
            )
        }

        this.lastStats = stats
    }
}

// Singleton instance
export const memoryMonitor = new MemoryMonitor()

// Auto-start monitoring when module is imported (only in production)
if (isServer && process.env.NODE_ENV === 'production') {
    memoryMonitor.start()
}
